import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";

// Layouts
import AdminLayout from "@/layouts/AdminLayout";
import UserLayout from "@/layouts/UserLayout";
import TenantLayout from "@/layouts/TenantLayout";

// Auth Pages
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import SuperAdminLogin from "@/pages/auth/SuperAdminLogin";

// Landing Pages
import NewLandingPage from "@/pages/NewLandingPage";
import LandingPage from "@/pages/LandingPage";

// Super Admin Pages
import SuperAdminDashboard from "@/pages/superadmin/Dashboard";

// Tenant Pages
import TenantDashboard from "@/pages/tenant/Dashboard";
import Billing from "@/pages/tenant/Billing";
import BillingSuccess from "@/pages/tenant/BillingSuccess";

// Admin Pages (existing)
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminUsers from "@/pages/admin/Users";
import AdminUserSession from "@/pages/admin/UserSession";
import AdminCalls from "@/pages/admin/Calls";
import AdminAlerts from "@/pages/admin/Alerts";
import AdminOneDrive from "@/pages/admin/OneDrive";
import AdminOneDriveInventory from "@/pages/admin/OneDriveInventory";
import AdminPipelineBot from "@/pages/admin/PipelineBot";
import AdminRepository from "@/pages/admin/Repository";
import AdminScoringGuide from "@/pages/admin/ScoringGuide";
import AdminActivityLog from "@/pages/admin/ActivityLog";
import AdminAPIConfiguration from "@/pages/admin/APIConfiguration";
import NotesCompliance from "@/pages/admin/Reports";
import AdminKPI from "@/pages/admin/KPI";
import AdminLibrary from "@/pages/admin/Library";
import AdminLeaderboard from "@/pages/admin/Leaderboard";
import AdminDocumentation from "@/pages/admin/Documentation";
import AdminSearchApplicants from "@/pages/admin/SearchApplicants";
import AdminMasterDelete from "@/pages/admin/MasterDelete";
import AdminSettings from "@/pages/admin/Settings";
import AdminSupport from "@/pages/admin/Support";
import AdminTeam from "@/pages/admin/Team";
import AdminTeamReports from "@/pages/admin/TeamReportsEnhanced";
import AudioDecodingSettings from "@/pages/admin/AudioDecodingSettings";
import AdminFineTuneLLM from "@/pages/admin/FineTuneLLM";
import AdminQANotes from "@/pages/admin/QANotes";
import AuditTrail from "@/pages/admin/AuditTrail";
import ProcessConfig from "@/pages/admin/ProcessConfig";
import AnalyticsDashboard from "@/pages/admin/AnalyticsDashboard";
import QATaskManagement from "@/pages/admin/QATaskManagement";
import CaseStudies from "@/pages/admin/CaseStudies";
import ReprocessCalls from "@/pages/admin/ReprocessCalls";
import RoleManagement from "@/pages/admin/RoleManagement";
import LocationManagement from "@/pages/admin/LocationManagement";
import ReviewRequests from "@/pages/admin/ReviewRequests";
import Submissions from "@/pages/admin/Submissions";
import AuditorsDashboard from "@/pages/admin/AuditorsDashboard";
import AICommCoach from "@/pages/admin/AICommCoach";
import MockTraining from "@/pages/admin/MockTraining";

// User Pages (existing)
import UserDashboard from "@/pages/user/Dashboard";
import UserCalls from "@/pages/user/Calls";
import UserLibrary from "@/pages/user/Library";
import UserLeaderboard from "@/pages/user/Leaderboard";
import UserSearchApplicants from "@/pages/user/SearchApplicants";
import UserRepository from "@/pages/user/Repository";
import UserScoringGuide from "@/pages/user/ScoringGuide";
import UserReports from "@/pages/user/Reports";
import UserKPI from "@/pages/user/KPI";
import UserSupport from "@/pages/user/Support";
import UserTeam from "@/pages/user/Team";
import UserDocumentation from "@/pages/user/Documentation";
import UserAnalytics from "@/pages/user/Analytics";
import UserQANotes from "@/pages/user/QANotes";
import UserAICommCoach from "@/pages/user/AICommCoach";

// Shared Pages
import CallDetail from "@/pages/CallDetail";

// EEOC Training Pages
import EEOCDashboard from "@/pages/eeoc/EEOCDashboard";
import EEOCTestWelcome from "@/pages/eeoc/EEOCTestWelcome";
import EEOCVoiceAssessment from "@/pages/eeoc/EEOCVoiceAssessment";
import EEOCResults from "@/pages/eeoc/EEOCResults";
import EEOCGuide from "@/pages/eeoc/EEOCGuide";
import EEOCScenarioHelper from "@/pages/eeoc/EEOCScenarioHelper";
import EEOCScenarioBook from "@/pages/eeoc/EEOCScenarioBook";

// User EEOC Pages
import UserEEOC from "@/pages/user/UserEEOC";
import UserEEOCMockTest from "@/pages/user/UserEEOCMockTest";
import UserEEOCAssessment from "@/pages/user/UserEEOCAssessment";
import UserMockTraining from "@/pages/user/UserMockTraining";

import "@/App.css";

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <Routes>
          {/* New SaaS Landing Page */}
          <Route path="/" element={<NewLandingPage />} />
          
          {/* Auth Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/super-admin" element={<SuperAdminLogin />} />
          
          {/* Super Admin Routes */}
          <Route path="/super-admin/dashboard" element={<SuperAdminDashboard />} />
          
          {/* New Tenant Dashboard Routes */}
          <Route path="/dashboard" element={<TenantLayout />}>
            <Route index element={<AnalyticsDashboard />} />
            <Route path="calls" element={<AdminCalls />} />
            <Route path="alerts" element={<AdminAlerts />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="user-session" element={<AdminUserSession />} />
            <Route path="reports" element={<NotesCompliance />} />
            <Route path="kpi" element={<AdminKPI />} />
            <Route path="library" element={<AdminLibrary />} />
            <Route path="leaderboard" element={<AdminLeaderboard />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="billing" element={<Billing />} />
            <Route path="billing/success" element={<BillingSuccess />} />
            <Route path="coaching" element={<Navigate to="../ai-coach?tab=analytics" replace />} />
            <Route path="homework-ai" element={<Navigate to="../ai-coach?tab=tasks" replace />} />
            <Route path="repository" element={<AdminRepository />} />
            <Route path="scoring-guide" element={<AdminScoringGuide />} />
            <Route path="activity" element={<AdminActivityLog />} />
            <Route path="support" element={<AdminSupport />} />
            <Route path="team" element={<AdminTeam />} />
            <Route path="team-reports" element={<AdminTeamReports />} />
            <Route path="audio-settings" element={<AudioDecodingSettings />} />
            <Route path="search-applicants" element={<AdminSearchApplicants />} />
            <Route path="documentation" element={<AdminDocumentation />} />
            <Route path="onedrive" element={<AdminOneDrive />} />
            <Route path="onedrive-inventory" element={<AdminOneDriveInventory />} />
            <Route path="pipeline-bot" element={<AdminPipelineBot />} />
            <Route path="master-delete" element={<AdminMasterDelete />} />
            <Route path="fine-tune-llm" element={<AdminFineTuneLLM />} />
            <Route path="qa-notes" element={<AdminQANotes />} />
            <Route path="audit-trail" element={<AuditTrail />} />
            <Route path="process-config" element={<ProcessConfig />} />
            <Route path="analytics" element={<AnalyticsDashboard />} />
            <Route path="qa-tasks" element={<QATaskManagement />} />
            <Route path="auditors-dashboard" element={<AuditorsDashboard />} />
            <Route path="qa-dashboard" element={<AuditorsDashboard />} />
            <Route path="case-studies" element={<CaseStudies />} />
            <Route path="case-studies/:caseStudyId" element={<CaseStudies />} />
            <Route path="reprocess-calls" element={<ReprocessCalls />} />
            <Route path="review-requests" element={<ReviewRequests />} />
            <Route path="role-management" element={<RoleManagement />} />
            <Route path="location-management" element={<LocationManagement />} />
            <Route path="submissions" element={<Submissions />} />
            <Route path="communication-coach" element={<Navigate to="../ai-coach?tab=practice" replace />} />
            <Route path="coach-dashboard" element={<Navigate to="../ai-coach?tab=dashboard" replace />} />
            <Route path="ai-coach" element={<AICommCoach />} />
            <Route path="mock-training" element={<MockTraining />} />
            <Route path="eeoc" element={<EEOCDashboard />} />
            <Route path="eeoc/mock" element={<UserEEOCMockTest />} />
            <Route path="eeoc/assessment" element={<UserEEOCAssessment />} />
            <Route path="eeoc/training" element={<UserEEOC />} />
            <Route path="eeoc/scenarios" element={<EEOCScenarioBook />} />
            <Route path="call/:callId" element={<CallDetail isAdmin />} />
          </Route>
          
          {/* EEOC Public Test Routes (no auth required) */}
          <Route path="/eeoc/test/:linkId" element={<EEOCTestWelcome />} />
          <Route path="/eeoc/test/:linkId/mock" element={<EEOCVoiceAssessment isMock={true} />} />
          <Route path="/eeoc/test/:linkId/assessment" element={<EEOCVoiceAssessment isMock={false} />} />
          <Route path="/eeoc/test/:linkId/results" element={<EEOCResults />} />
          <Route path="/eeoc/test/:linkId/guide" element={<EEOCGuide />} />
          <Route path="/eeoc/test/:linkId/scenarios" element={<EEOCScenarioHelper />} />
          
          {/* Legacy Admin Routes (for backward compatibility) */}
          <Route path="/legacy" element={<LandingPage />} />
          <Route path="/admin/:adminUrl" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="user-session" element={<AdminUserSession />} />
            <Route path="calls" element={<AdminCalls />} />
            <Route path="alerts" element={<AdminAlerts />} />
            <Route path="repository" element={<AdminRepository />} />
            <Route path="scoring-guide" element={<AdminScoringGuide />} />
            <Route path="activity" element={<AdminActivityLog />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="reports" element={<NotesCompliance />} />
            <Route path="kpi" element={<AdminKPI />} />
            <Route path="coaching" element={<Navigate to="../ai-coach?tab=analytics" replace />} />
            <Route path="homework-ai" element={<Navigate to="../ai-coach?tab=tasks" replace />} />
            <Route path="library" element={<AdminLibrary />} />
            <Route path="leaderboard" element={<AdminLeaderboard />} />
            <Route path="search-applicants" element={<AdminSearchApplicants />} />
            <Route path="documentation" element={<AdminDocumentation />} />
            <Route path="onedrive" element={<AdminOneDrive />} />
            <Route path="master-delete" element={<AdminMasterDelete />} />
            <Route path="support" element={<AdminSupport />} />
            <Route path="team" element={<AdminTeam />} />
            <Route path="team-reports" element={<AdminTeamReports />} />
            <Route path="analytics" element={<AnalyticsDashboard />} />
            <Route path="audio-settings" element={<AudioDecodingSettings />} />
            <Route path="review-requests" element={<ReviewRequests />} />
            <Route path="auditors-dashboard" element={<AuditorsDashboard />} />
            <Route path="qa-dashboard" element={<AuditorsDashboard />} />
            <Route path="communication-coach" element={<Navigate to="../ai-coach?tab=practice" replace />} />
            <Route path="coach-dashboard" element={<Navigate to="../ai-coach?tab=dashboard" replace />} />
            <Route path="ai-coach" element={<AICommCoach />} />
            <Route path="call/:callId" element={<CallDetail isAdmin />} />
          </Route>
          
          {/* User Routes (for individual user unique URLs) */}
          <Route path="/user/:userUrl" element={<UserLayout />}>
            <Route index element={<UserAnalytics />} />
            <Route path="calls" element={<UserCalls />} />
            <Route path="coaching" element={<Navigate to="../ai-coach?tab=analytics" replace />} />
            <Route path="homework-ai" element={<Navigate to="../ai-coach?tab=tasks" replace />} />
            <Route path="communication-coach" element={<Navigate to="../ai-coach?tab=practice" replace />} />
            <Route path="coach-dashboard" element={<Navigate to="../ai-coach?tab=dashboard" replace />} />
            <Route path="ai-coach" element={<UserAICommCoach />} />
            <Route path="library" element={<UserLibrary />} />
            <Route path="leaderboard" element={<UserLeaderboard />} />
            <Route path="search-applicants" element={<UserSearchApplicants />} />
            <Route path="repository" element={<UserRepository />} />
            <Route path="scoring-guide" element={<UserScoringGuide />} />
            <Route path="reports" element={<UserReports />} />
            <Route path="kpi" element={<UserKPI />} />
            <Route path="analytics" element={<UserAnalytics />} />
            <Route path="qa-notes" element={<UserQANotes />} />
            <Route path="communication-coach" element={<Navigate to="../ai-coach?tab=practice" replace />} />
            <Route path="coach-dashboard" element={<Navigate to="../ai-coach?tab=dashboard" replace />} />
            <Route path="ai-coach" element={<UserAICommCoach />} />
            <Route path="support" element={<UserSupport />} />
            <Route path="team" element={<UserTeam />} />
            <Route path="documentation" element={<UserDocumentation />} />
            <Route path="call/:callId" element={<CallDetail />} />
            <Route path="eeoc" element={<UserEEOC />} />
            <Route path="eeoc/mock" element={<UserEEOCMockTest />} />
            <Route path="eeoc/assessment" element={<UserEEOCAssessment />} />
            <Route path="eeoc/scenarios" element={<EEOCScenarioBook />} />
            <Route path="mock-training" element={<UserMockTraining />} />
          </Route>
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  </ThemeProvider>
  );
}

export default App;
